export class ClusterRateLimiter {
  private lastSecondStart: number;
  private playedThisSecond = 0;

  private clusterLastSecondStart = new Map<string, number>();
  private clusterPlayedThisSecond = new Map<string, number>();

  private pendingByCluster = new Map<string, number>();

  constructor(
    private readonly globalMaxPerSec: number,
    private readonly perClusterMaxPerSec: number,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.lastSecondStart = this.now();
  }

  /** A clustered sound joins its group's burst; the next flush plays one representative per group. */
  public request(clusterGroup: string): void {
    const prev = this.pendingByCluster.get(clusterGroup) ?? 0;
    this.pendingByCluster.set(clusterGroup, prev + 1);
  }

  /** The groups whose representative may play now, and how many were refused by the per-second limits. */
  public flush(): { allowed: string[]; refused: number } {
    const groups = [...this.pendingByCluster.keys()];
    this.pendingByCluster.clear();

    const allowed: string[] = [];

    for (const group of groups) {
      if (!this.consumeGlobal()) continue;
      if (!this.consumeCluster(group)) continue;
      allowed.push(group);
    }

    return { allowed, refused: groups.length - allowed.length };
  }

  private consumeGlobal(): boolean {
    const now = this.now();
    if (now - this.lastSecondStart > 1000) {
      this.lastSecondStart = now;
      this.playedThisSecond = 0;
    }

    if (this.playedThisSecond >= this.globalMaxPerSec) return false;
    this.playedThisSecond++;
    return true;
  }

  private consumeCluster(group: string): boolean {
    const now = this.now();
    const lastStart = this.clusterLastSecondStart.get(group);

    // The window's start is stored on first use and whenever it rolls over; without the first
    // store the count never reset and a group was refused for good after its per-second cap.
    if (lastStart === undefined || now - lastStart > 1000) {
      this.clusterLastSecondStart.set(group, now);
      this.clusterPlayedThisSecond.set(group, 0);
    }

    const played = this.clusterPlayedThisSecond.get(group) ?? 0;
    if (played >= this.perClusterMaxPerSec) return false;

    this.clusterPlayedThisSecond.set(group, played + 1);
    return true;
  }
}